import { ReactNode, useId, useState } from 'react';
import { ImageWithFallback } from '../../components/ui/ImageWithFallback';
import { formatCurrency } from '../../utils/currency';
import { CheckoutTotals } from '../../utils/pricing';
import { CartItem } from '../../models/cart';
import { ChevronDownIcon } from './CheckoutIcons';

export function OrderItemsList({ items, title = 'Seu pedido' }: { items: CartItem[]; title?: string }) {
  return (
    <div>
      <p className="text-sm font-bold text-chocolate-dark mb-2.5">{title}</p>
      <div className="flex flex-col gap-2.5 max-h-56 overflow-y-auto pr-1">
        {items.map((item) => {
          const unitPrice = item.promotionalPrice ?? item.price;
          return (
            <div key={`${item.productId}-${item.variantId}`} className="flex items-center gap-2.5">
              <ImageWithFallback
                src={item.imageUrl}
                alt={item.productName}
                className="w-10 h-10 rounded-lg flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-text-dark truncate">{item.productName}</p>
                <p className="text-xs text-text-medium truncate">
                  {item.variantLabel} · {item.quantity}x
                </p>
              </div>
              <span className="text-sm font-semibold text-text-dark flex-shrink-0">
                R$ {formatCurrency(unitPrice * item.quantity)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function OrderTotals({ totals }: { totals: CheckoutTotals }) {
  return (
    <div>
      {totals.pixDiscountApplies && (
        <div className="flex flex-col gap-1 mb-2 text-sm">
          <div className="flex justify-between text-text-medium">
            <span>Produtos</span>
            <span>R$ {formatCurrency(totals.gross)}</span>
          </div>
          <div className="flex justify-between font-semibold text-text-dark">
            <span>Desconto Pix</span>
            <span>−R$ {formatCurrency(totals.pixDiscount)}</span>
          </div>
        </div>
      )}
      <div className="flex justify-between items-baseline gap-2">
        <span className="text-sm font-medium text-text-dark">Total dos produtos</span>
        <span className="text-lg font-bold text-text-dark">R$ {formatCurrency(totals.net)}</span>
      </div>
      <p className="text-sm text-text-medium mt-0.5">Frete: a vendedora confirma pelo WhatsApp</p>
    </div>
  );
}

interface OrderSummaryCardProps {
  items: CartItem[];
  totals: CheckoutTotals;
  children?: ReactNode;
}

export function OrderSummaryCard({ items, totals, children }: OrderSummaryCardProps) {
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-champagne-dark">
      <OrderItemsList items={items} />
      <div className="border-t border-champagne-dark/30 my-3" />
      <OrderTotals totals={totals} />
      {children}
    </div>
  );
}

interface MobileOrderSummaryProps {
  items: CartItem[];
  totals: CheckoutTotals;
}

export function MobileOrderSummary({ items, totals }: MobileOrderSummaryProps) {
  const [expanded, setExpanded] = useState(false);
  const panelId = useId();
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="lg:hidden mb-4 bg-white rounded-xl border border-champagne-dark shadow-sm">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => setExpanded((v) => !v)}
        className="w-full min-h-[48px] flex items-center justify-between gap-3 px-4 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate rounded-xl"
      >
        <span className="text-sm font-semibold text-chocolate-dark">
          Resumo do pedido ({itemCount} {itemCount === 1 ? 'item' : 'itens'})
        </span>
        <span className="flex items-center gap-2 text-sm font-bold text-text-dark">
          R$ {formatCurrency(totals.net)}
          <ChevronDownIcon
            className={`w-4 h-4 text-chocolate-dark transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          />
        </span>
      </button>
      {expanded && (
        <div id={panelId} className="px-4 pb-4 border-t border-champagne-dark/40 pt-3">
          <OrderItemsList items={items} title="Itens" />
          <div className="border-t border-champagne-dark/30 my-3" />
          <OrderTotals totals={totals} />
        </div>
      )}
    </div>
  );
}
